// ChatWindow.js
import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import FileDropZone from './shared/FileDropZone';
import './ChatWindow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../utils/axiosConfig';
import { OfferContext } from '../OfferContext'; // Adjust the import path if necessary
import { AuthContext } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorMessages';

// Add this helper function to convert markdown-style bold to HTML
const formatMessageLine = (line) => {
  // Convert **text** to <strong>text</strong> and newlines to <br>
  return line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>');
};

function ChatWindow({ isTradeRecap = false, recapType = null, persistOfferId = null, persistRecapId = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasMicPermission, setHasMicPermission] = useState(null);
  const [hasMicCapability, setHasMicCapability] = useState(null);
  const [audioStream, setAudioStream] = useState(null);
  const [audioData, setAudioData] = useState([]);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const animationFrameRef = useRef(null);
  const { setOfferResponse, offerResponse } = useContext(OfferContext);
  const { user } = useContext(AuthContext);
  const [fileName, setFileName] = useState('');
  
  // Get deal type from URL query parameter
  const dealType = new URLSearchParams(window.location.search).get('dealType') || 'Offer';

  // Check if device has microphone capability
  useEffect(() => {
    if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices()
        .then(devices => {
          const hasMic = devices.some(device => device.kind === 'audioinput');
          setHasMicCapability(hasMic);
          console.log('Microphone capability:', hasMic);
        })
        .catch(err => {
          console.error('Error checking microphone capability:', err);
          setHasMicCapability(false);
        });
    } else {
      setHasMicCapability(false);
      console.log('MediaDevices API not supported');
    }
  }, []);

  // Warm the chat LLM endpoint on open so the first reply doesn't wait on a cold
  // GPU boot. Best-effort and debounced server-side; failure is silent.
  useEffect(() => {
    axiosInstance.post('/api/knowledge-hub/warm-llm/').catch(() => {});
  }, []);

  // Clean up audio resources when component unmounts
  useEffect(() => {
    return () => {
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [audioStream]);

  // One AudioContext, built on first recording and reused. Guarded so a device
  // that refuses audio returns null and voice degrades instead of throwing.
  const getAudioContext = useCallback(() => {
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      return audioContextRef.current;
    }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (!Ctor) return null;
      audioContextRef.current = new Ctor();
      return audioContextRef.current;
    } catch (error) {
      console.warn('AudioContext unavailable, voice features disabled:', error);
      audioContextRef.current = null;
      return null;
    }
  }, []);

  const toggleRecording = async () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const startRecording = async () => {
    try {
      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setAudioStream(stream);
      setHasMicPermission(true);
      
      // Reuse the shared audio context. This used to build a new one on every
      // recording and overwrite the ref without closing the previous context,
      // which orphaned them against the browser's ~6-per-document cap.
      const audioContext = getAudioContext();
      if (!audioContext) {
        throw new Error('Audio is unavailable on this device');
      }
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      
      // Set up media recorder
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        audioChunksRef.current = [];
        processAudioData(audioBlob);
      };
      
      // Add initial recording message
      setMessages(prev => [...prev, { 
        text: 'Processing audio...',
        sender: 'user'
      }]);
      
      // Start recording
      mediaRecorder.start();
      setIsRecording(true);
      
      // Start visualizing audio
      visualizeAudio();
      
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        setHasMicPermission(false);
        alert('Microphone permission denied. Please allow microphone access to use this feature.');
      } else {
        alert('Error starting recording. Please try again.');
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      
      if (audioStream) {
        audioStream.getTracks().forEach(track => track.stop());
        setAudioStream(null);
      }
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      setIsRecording(false);
      setAudioData([]);
    }
  };
  
  const visualizeAudio = () => {
    if (!analyserRef.current) return;
    
    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    
    const updateVisualization = () => {
      if (!analyserRef.current) return;
      
      analyserRef.current.getByteFrequencyData(dataArray);
      
      // We'll use a subset of the frequency data for visualization
      const visualData = Array.from(dataArray.slice(0, 20))
        .map(value => value / 255); // Normalize to 0-1
        
      setAudioData(visualData);
      
      animationFrameRef.current = requestAnimationFrame(updateVisualization);
    };
    
    updateVisualization();
  };
  
  const processAudioData = async (audioBlob) => {
    try {
      setIsLoading(true);
      
      // Create form data to send to backend
      const formData = new FormData();
      formData.append('voice_file', audioBlob, 'recording.wav');
      formData.append('input', '');
      formData.append('deal_type', dealType); // Add deal type from URL
      
      // Add recap type if available and this is a trade recap
      if (isTradeRecap && recapType) {
        formData.append('recap_type', recapType);
      }
      
      // Use different endpoints based on whether this is a trade recap or not
      let endpoint;
      if (isTradeRecap) {
        if (recapType === 'Freight') {
          endpoint = '/api/offer/process-freight-input/';
        } else {
          endpoint = '/api/offer/process-recap-input/';
        }
      } else {
        endpoint = '/api/offer/process-input/';
      }
      
      const response = await axiosInstance.post(
        endpoint,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      // Update the last message (which should be the "Processing audio..." message)
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0) {
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.text === 'Processing audio...') {
            lastMessage.text = response.data.transcribed_text || 'Could not transcribe audio';
          }
        }
        return newMessages;
      });
      
      // Handle response
      if (response.data.error) {
        const errorMsg = response.data.error === 'No offer details extracted from input.' 
          ? 'Please input Deal details text' 
          : getErrorMessage({ response }, 'Message could not be processed. Please try again.');
        setMessages(prev => [...prev, { 
          text: errorMsg,
          sender: 'assistant'
        }]);
      } else {
        let formattedResponse;
        if (isTradeRecap) {
          if (recapType === 'Freight') {
            formattedResponse = formatFreightRecapResponse(response.data);
          } else {
            formattedResponse = formatTradeRecapResponse(response.data);
          }
        } else {
          formattedResponse = withReadingNote(formatOfferResponse(response.data), response.data);
        }
        
        setMessages(prev => [...prev, {
          text: formattedResponse,
          sender: 'assistant'
        }]);
        setOfferResponse(response.data);
      }
      
    } catch (error) {
      console.error('Error processing audio:', error);
      setMessages(prev => [...prev, { 
        text: getErrorMessage(error, 'Message could not be processed. Please try again.'),
        sender: 'assistant'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // The deal chat reads only the first pages of a long PDF: a scanned page takes
  // seconds to read and the trader is waiting on the reply. Say so, so a deal
  // built from the first pages is not taken for one built from all of them.
  const withReadingNote = (formatted, data) => {
    const pages = data && data._pages_read_limited_to;
    if (!pages) return formatted;
    return `${formatted}\n\n**Note**: Only the first ${pages} pages of this document were read. `
      + 'If the deal terms are further in, paste them into the chat.';
  };

  // Function to format the offer response
  function formatOfferResponse(offerResponse) {
    let message = '';
  
    // Seller
    if (offerResponse.Seller && offerResponse.Seller['Selling Entity']) {
      message += `**Seller**: ${offerResponse.Seller['Selling Entity']}\n`;
    }
  
    // Buyer
    if (offerResponse.Buyer && offerResponse.Buyer["Buyer's Entity"]) {
      message += `**Buyer**: ${offerResponse.Buyer["Buyer's Entity"]}\n`;
    }
  
    // Incoterms
    if (offerResponse.Incoterms && offerResponse.Incoterms['Incoterm']) {
      message += `**Incoterms**: ${offerResponse.Incoterms['Incoterm']}\n`;
    }
  
    // Commodity
    if (offerResponse.Commodity) {
      if (offerResponse.Commodity['Product']) {
        message += `**Product**: ${offerResponse.Commodity['Product']}\n`;
      }
      if (offerResponse.Commodity['Origin']) {
        message += `**Origin**: ${offerResponse.Commodity['Origin']}\n`;
      }
      if (offerResponse.Commodity['Additional Information']) {
        message += `**Additional Information**: ${offerResponse.Commodity['Additional Information']}\n`;
      }

    }
  
    // Quantity
    if (offerResponse.Quantity) {
      // Support both old and new field names for backward compatibility
      const quantityValue = offerResponse.Quantity['Quantity'] || offerResponse.Quantity['Quantity (metric tons)'];
      if (quantityValue) {
        const unit = offerResponse.Quantity['Unit'];
        // Only show unit if it was validated and populated
        if (unit) {
          message += `**Quantity**: ${quantityValue} ${unit}\n`;
        } else {
          message += `**Quantity**: ${quantityValue}\n`;
        }
      }
    }

    // Tolerance (top-level fields)
    if (offerResponse['Tolerance Upper'] !== undefined && offerResponse['Tolerance Upper'] !== null) {
      const upperUnit = offerResponse['Tolerance Upper Unit'] || '%';
      message += `**Tolerance Upper**: ${offerResponse['Tolerance Upper']}${upperUnit === '%' ? '%' : ' (' + upperUnit + ')'}\n`;
    }
    if (offerResponse['Tolerance Lower'] !== undefined && offerResponse['Tolerance Lower'] !== null) {
      const lowerUnit = offerResponse['Tolerance Lower Unit'] || '%';
      message += `**Tolerance Lower**: ${offerResponse['Tolerance Lower']}${lowerUnit === '%' ? '%' : ' (' + lowerUnit + ')'}\n`;
    }

    // Quantity continued
    if (offerResponse.Quantity) {
      if (offerResponse.Quantity['Quantity Option']) {
        message += `**Quantity Option**: ${offerResponse.Quantity['Quantity Option']}\n`;
      }
    }
  
    // Delivery
    if (offerResponse.Delivery && offerResponse.Delivery['Delivery']) {
      message += `**Delivery**: ${offerResponse.Delivery['Delivery']}\n`;
    }
    if (offerResponse.Delivery && offerResponse.Delivery['Laycan Start']) {
      const laycanEnd = offerResponse.Delivery['Laycan End'] || '';
      message += `**Laycan**: ${offerResponse.Delivery['Laycan Start']}${laycanEnd ? ` to ${laycanEnd}` : ''}\n`;
    }
  
    // Loading Ports
    if (offerResponse['Loading Port(s)'] && offerResponse['Loading Port(s)']['Loading Port(s)']) {
      message += `**Loading Port(s)**: ${offerResponse['Loading Port(s)']['Loading Port(s)']}\n`;
    }

    if (offerResponse['Discharging Port(s)'] && offerResponse['Discharging Port(s)']['Discharging Port(s)']) {
      message += `**Discharging Port(s)**: ${offerResponse['Discharging Port(s)']['Discharging Port(s)']}\n`;
    }
  
    // Base Price
    if (offerResponse['Base Price']) {
      if (offerResponse['Base Price']['Price type']) {
        message += `**Price Type**: ${offerResponse['Base Price']['Price type']}\n`;
      }
      if (offerResponse['Base Price']['Currency']) {
        message += `**Currency**: ${offerResponse['Base Price']['Currency']}\n`;
      }
      if (offerResponse['Base Price']['For Fixed Price']) {
        message += `**For Fixed Price**: ${offerResponse['Base Price']['For Fixed Price']}\n`;
      }
      if (offerResponse['Base Price']['For Index: Premium/Discount']) {
        message += `**For Index: Premium/Discount**: ${offerResponse['Base Price']['For Index: Premium/Discount']}\n`;
      }
      if (offerResponse['Base Price']['CV Basis']) {
        message += `**CV Basis**: ${offerResponse['Base Price']['CV Basis']}\n`;
      }
      if (offerResponse['Base Price']['Kcal/Kg']) {
        message += `**Kcal/Kg**: ${offerResponse['Base Price']['Kcal/Kg']}\n`;
      }
      if (offerResponse['Base Price']['Applicable Index Period']) {
        message += `**Applicable Index Period**: ${offerResponse['Base Price']['Applicable Index Period']}\n`;
      }
      if (offerResponse['Base Price']['Overwrite Base Price']) {
        message += `**Overwrite Base Price**: ${offerResponse['Base Price']['Overwrite Base Price']}\n`;
      }
    }
  
    // Quality Table
    if (offerResponse['Quality Table'] && offerResponse['Quality Table']['Quality Values'] && offerResponse['Quality Table']['Quality Values'].length > 0) {
      message += `\n**Quality Table**:\n`;
      
      // Add column headers
      const typicalColumnName = offerResponse['Quality Table']['Typical Column Name'] || 'Typical';
      const minMaxColumnName = offerResponse['Quality Table']['MinMax Column Name'] || 'Min/Max';
      
      message += `Parameter | ${typicalColumnName} | ${minMaxColumnName}\n`;
      message += `--------- | ${'-'.repeat(typicalColumnName.length)} | ${'-'.repeat(minMaxColumnName.length)}\n`;
      
      // Add rows
      offerResponse['Quality Table']['Quality Values'].forEach(value => {
        // For parameters with options, use the selected option as the display name
        // Otherwise use the parameter name
        const paramName = value['Selected Option'] || value['Parameter Name'] || '';
        const typicalValue = value['Typical/Guaranteed Value'] || '';
        const minMaxValue = value['Min/Max or Rejection Value'] || '';
        
        message += `${paramName} | ${typicalValue} | ${minMaxValue}\n`;
      });
      
      message += '\n';
    }
    // New format for quality table
    else if (offerResponse.quality_table && offerResponse.quality_table.quality_values && offerResponse.quality_table.quality_values.length > 0) {
      message += `\n**Quality Table**:\n`;
      
      // Add column headers
      const typicalColumnName = offerResponse.quality_table.typical_column_name || 'Typical';
      const minMaxColumnName = offerResponse.quality_table.min_max_column_name || 'Min/Max';
      
      message += `Parameter | ${typicalColumnName} | ${minMaxColumnName}\n`;
      message += `--------- | ${'-'.repeat(typicalColumnName.length)} | ${'-'.repeat(minMaxColumnName.length)}\n`;
      
      // Add rows
      offerResponse.quality_table.quality_values.forEach(value => {
        // For parameters with options, use the selected option as the display name
        // Otherwise use the parameter name
        const paramName = value.selected_option || value.parameter_name || '';
        const typicalValue = value.typical_or_guaranteed_value || '';
        const minMaxValue = value.min_max_or_rejection_value || '';
        
        message += `${paramName} | ${typicalValue} | ${minMaxValue}\n`;
      });
      
      message += '\n';
    }
  
    // Quality Adjustments
    if (offerResponse['Quality Adjustments']) {
      if (offerResponse['Quality Adjustments']['Pro-rata CV adjustments']) {
        message += `**Pro-rata CV adjustments**: ${offerResponse['Quality Adjustments']['Pro-rata CV adjustments']}\n`;
      }
      if (offerResponse['Quality Adjustments']['Other Premium/Penalties']) {
        message += `**Other Premium/Penalties**: ${offerResponse['Quality Adjustments']['Other Premium/Penalties']}\n`;
      }
    }
  
    // Payment
    if (offerResponse.Payment) {
      if (offerResponse.Payment['Payment Method']) {
        message += `**Payment Method**: ${offerResponse.Payment['Payment Method']}\n`;
      }
      if (offerResponse.Payment['Payment Wording']) {
        message += `**Payment Wording**: ${offerResponse.Payment['Payment Wording']}\n`;
      }
    }
  
    // Quality and Quantity Determination
    if (offerResponse['Quality and Quantity Determination']) {
      if (offerResponse['Quality and Quantity Determination']['Surveyor(s) or leave empty for generic wording']) {
        message += `**Surveyor(s) or Generic Wording**: ${offerResponse['Quality and Quantity Determination']['Surveyor(s) or leave empty for generic wording']}\n`;
      }
      if (offerResponse['Quality and Quantity Determination']['Overwrite Quality and Quantity Determination']) {
        message += `**Overwrite Quality and Quantity Determination**: ${offerResponse['Quality and Quantity Determination']['Overwrite Quality and Quantity Determination']}\n`;
      }
    }
  
    // Shipping Terms
    if (offerResponse['Shipping Terms']) {
      if (offerResponse['Shipping Terms']['Shipping Terms']) {
        message += `**Shipping Terms**: ${offerResponse['Shipping Terms']['Shipping Terms']}\n`;
      }
      if (offerResponse['Shipping Terms']['Shipping Terms Wording']) {
        message += `**Shipping Terms Wording**: ${offerResponse['Shipping Terms']['Shipping Terms Wording']}\n`;
      }
    }
  
    // Other Terms
    if (offerResponse['Other Terms']) {
      if (offerResponse['Other Terms']['Other Terms']) {
        message += `**Other Terms**: ${offerResponse['Other Terms']['Other Terms']}\n`;
      }
      if (offerResponse['Other Terms']['Other Terms Wording']) {
        message += `**Other Terms Wording**: ${offerResponse['Other Terms']['Other Terms Wording']}\n`;
      }
    }
  
    // Deal Validity
    if (offerResponse['Deal Validity']) {
      if (offerResponse['Deal Validity']['Date']) {
        message += `**Deal Validity Date**: ${offerResponse['Deal Validity']['Date']}\n`;
      }
      if (offerResponse['Deal Validity']['Time']) {
        message += `**Deal Validity Time**: ${offerResponse['Deal Validity']['Time']}\n`;
      }
      if (offerResponse['Deal Validity']['Time-Zone']) {
        message += `**Deal Validity Time-Zone**: ${offerResponse['Deal Validity']['Time-Zone']}\n`;
      }
    }
  
    // Error
    if (offerResponse.error) {
      message += `**Error**: ${offerResponse.error}\n`;
    }
  
    // Return the formatted message or a default message
    return message.trim() || 'No deal details available.';
  }
  
  // Function to format the trade recap response
  function formatTradeRecapResponse(recapResponse) {
    let message = '';
    
    // Core Fields
    
    if (recapResponse['entity']) {
      message += `**Entity**: ${recapResponse['entity']}\n`;
    }
    
    if (recapResponse['product']) {
      message += `**Product**: ${recapResponse['product']}\n`;
    }
    
    if (recapResponse['origin']) {
      message += `**Origin**: ${recapResponse['origin']}\n`;
    }

    if (recapResponse['laycan']) {
      message += `**Laycan**: ${recapResponse['laycan']}\n`;
    }
    
    if (recapResponse['laycan_comments']) {
      message += `**Laycan Comments**: ${recapResponse['laycan_comments']}\n`;
    }
    
    // Display all fields regardless of recap_type
    // Sale Fields
    
    if (recapResponse['sale_buyer']) {
      message += `**Sale Buyer**: ${recapResponse['sale_buyer']}\n`;
    }

    if (recapResponse['sale_chain_in_between']) {
      message += `**Sale Chain In Between**: ${recapResponse['sale_chain_in_between']}\n`;
    }

    if (recapResponse['sale_enduser']) {
      message += `**Sale Enduser**: ${recapResponse['sale_enduser']}\n`;
    }
    
    if (recapResponse['sale_contract_quantity']) {
      message += `**Sale Contract Quantity**: ${recapResponse['sale_contract_quantity']}\n`;
    }
    
    // Handle shipping tolerance - check for separate lower/upper values first
    if (recapResponse['sale_shipping_tolerance_lower'] && recapResponse['sale_shipping_tolerance_upper']) {
      message += `**Sale Shipping Tolerance**: ${recapResponse['sale_shipping_tolerance_lower']} to ${recapResponse['sale_shipping_tolerance_upper']}\n`;
    } else if (recapResponse['sale_shipping_tolerance_lower']) {
      message += `**Sale Shipping Tolerance Lower**: ${recapResponse['sale_shipping_tolerance_lower']}\n`;
    } else if (recapResponse['sale_shipping_tolerance_upper']) {
      message += `**Sale Shipping Tolerance Upper**: ${recapResponse['sale_shipping_tolerance_upper']}\n`;
    } else if (recapResponse['sale_shipping_tolerance']) {
      message += `**Sale Shipping Tolerance**: ${recapResponse['sale_shipping_tolerance']}\n`;
    }
    
    if (recapResponse['sale_incoterm']) {
      message += `**Sale Incoterm**: ${recapResponse['sale_incoterm']}\n`;
    }
    
    if (recapResponse['sale_delivery_point']) {
      message += `**Sale Delivery Point**: ${recapResponse['sale_delivery_point']}\n`;
    }
    
    if (recapResponse['sale_contract_price_currency']) {
      message += `**Sale Contract Price Currency**: ${recapResponse['sale_contract_price_currency']}\n`;
    }
    
    if (recapResponse['sale_contract_price_value']) {
      message += `**Sale Contract Price Value**: ${recapResponse['sale_contract_price_value']}\n`;
    }
    
    if (recapResponse['sale_price_formula']) {
      message += `**Sale Price Formula**: ${recapResponse['sale_price_formula']}\n`;
    }
    
    if (recapResponse['sale_cv_basis_option']) {
      message += `**Sale CV Basis Option**: ${recapResponse['sale_cv_basis_option']}\n`;
    }
    
    if (recapResponse['sale_cv_basis_value']) {
      message += `**Sale CV Basis Value**: ${recapResponse['sale_cv_basis_value']}\n`;
    }
    
    if (recapResponse['sale_payment_terms']) {
      message += `**Sale Payment Terms**: ${recapResponse['sale_payment_terms']}\n`;
    }

    if (recapResponse['sale_commissions']) {
      message += `**Sale Commissions**: ${recapResponse['sale_commissions']}\n`;
    }

    if (recapResponse['sale_deal_makers']) {
      message += `**Sale Deal Makers**: ${recapResponse['sale_deal_makers']}\n`;
    }

    if (recapResponse['sale_deal_confirmation_date']) {
      message += `**Sale Deal Confirmation Date**: ${recapResponse['sale_deal_confirmation_date']}\n`;
    }
    
    if (recapResponse['sale_additional_comments']) {
      message += `**Sale Additional Comments**: ${recapResponse['sale_additional_comments']}\n`;
    }
    
    // Purchase Fields
    
    if (recapResponse['purchase_producer']) {
      message += `**Purchase Producer**: ${recapResponse['purchase_producer']}\n`;
    }

    if (recapResponse['purchase_chain_in_between']) { 
      message += `**Purchase Chain In Between**: ${recapResponse['purchase_chain_in_between']}\n`;
    }

    if (recapResponse['purchase_seller']) {
      message += `**Purchase Seller**: ${recapResponse['purchase_seller']}\n`;
    }
    
    if (recapResponse['purchase_contract_quantity']) {
      message += `**Purchase Contract Quantity**: ${recapResponse['purchase_contract_quantity']}\n`;
    }
    
    // Handle purchase shipping tolerance - check for separate lower/upper values first
    if (recapResponse['purchase_shipping_tolerance_lower'] && recapResponse['purchase_shipping_tolerance_upper']) {
      message += `**Purchase Shipping Tolerance**: ${recapResponse['purchase_shipping_tolerance_lower']} to ${recapResponse['purchase_shipping_tolerance_upper']}\n`;
    } else if (recapResponse['purchase_shipping_tolerance_lower']) {
      message += `**Purchase Shipping Tolerance Lower**: ${recapResponse['purchase_shipping_tolerance_lower']}\n`;
    } else if (recapResponse['purchase_shipping_tolerance_upper']) {
      message += `**Purchase Shipping Tolerance Upper**: ${recapResponse['purchase_shipping_tolerance_upper']}\n`;
    } else if (recapResponse['purchase_shipping_tolerance']) {
      message += `**Purchase Shipping Tolerance**: ${recapResponse['purchase_shipping_tolerance']}\n`;
    }
    
    if (recapResponse['purchase_incoterm']) {
      message += `**Purchase Incoterm**: ${recapResponse['purchase_incoterm']}\n`;
    }
    
    if (recapResponse['purchase_delivery_point']) {
      message += `**Purchase Delivery Point**: ${recapResponse['purchase_delivery_point']}\n`;
    }
    
    if (recapResponse['purchase_contract_price_currency']) {
      message += `**Purchase Contract Price Currency**: ${recapResponse['purchase_contract_price_currency']}\n`;
    }
    
    if (recapResponse['purchase_contract_price_value']) {
      message += `**Purchase Contract Price Value**: ${recapResponse['purchase_contract_price_value']}\n`;
    }
    
    if (recapResponse['purchase_price_formula']) {
      message += `**Purchase Price Formula**: ${recapResponse['purchase_price_formula']}\n`;
    }
    
    if (recapResponse['purchase_price_basis_option']) {
      message += `**Purchase Price Basis Option**: ${recapResponse['purchase_price_basis_option']}\n`;
    }
    
    if (recapResponse['purchase_price_basis_value']) {
      message += `**Purchase Price Basis Value**: ${recapResponse['purchase_price_basis_value']}\n`;
    }
    
    if (recapResponse['purchase_payment_terms']) {
      message += `**Purchase Payment Terms**: ${recapResponse['purchase_payment_terms']}\n`;
    }

    if (recapResponse['purchase_commissions']) {
      message += `**Purchase Commissions**: ${recapResponse['purchase_commissions']}\n`;
    }

    if (recapResponse['purchase_deal_makers']) {
      message += `**Purchase Deal Makers**: ${recapResponse['purchase_deal_makers']}\n`;
    }

    if (recapResponse['purchase_deal_confirmation_date']) {
      message += `**Purchase Deal Confirmation Date**: ${recapResponse['purchase_deal_confirmation_date']}\n`;
    }
    
    if (recapResponse['purchase_additional_comments']) {
      message += `**Purchase Additional Comments**: ${recapResponse['purchase_additional_comments']}\n`;
    }
    
    // Error
    if (recapResponse.error) {
      message += `**Error**: ${recapResponse.error}\n`;
    }
    
    // Return the formatted message or a default message
    return message.trim() || 'No trade recap details available.';
  }

  const formatFreightRecapResponse = (data) => {
    let formattedResponse = "**Freight Recap Details Extracted:**\n\n";
    
    // Core charter details
    if (data.entity) formattedResponse += `**Entity:** ${data.entity}\n`;
    if (data.charter_party_date) formattedResponse += `**Charter Party Date:** ${data.charter_party_date}\n`;
    if (data.product) formattedResponse += `**Product:** ${data.product}\n`;
    if (data.vessel) formattedResponse += `**Vessel:** ${data.vessel}\n`;
    if (data.quantity_mt) formattedResponse += `**Quantity:** ${data.quantity_mt} MT\n`;
    if (data.tolerance_pct) formattedResponse += `**Tolerance:** ${data.tolerance_pct}%\n`;
    if (data.load_port) formattedResponse += `**Load Port:** ${data.load_port}\n`;
    if (data.discharge_port) formattedResponse += `**Discharge Port:** ${data.discharge_port}\n`;
    if (data.laycan) formattedResponse += `**Laycan:** ${data.laycan}\n`;
    if (data.laycan_comments) formattedResponse += `**Laycan Comments:** ${data.laycan_comments}\n`;
    
    // Freight details
    if (data.freight_price) {
      formattedResponse += `**Freight Price:** ${data.freight_price} ${data.currency || 'USD'}\n`;
    }
    if (data.currency && !data.freight_price && data.currency !== 'USD') {
      formattedResponse += `**Currency:** ${data.currency}\n`;
    }
    if (data.payment_terms) formattedResponse += `**Payment Terms:** ${data.payment_terms}\n`;
    if (data.demurrage_dispatch) formattedResponse += `**Demurrage/Dispatch:** ${data.demurrage_dispatch}\n`;
    
    // Parties
    if (data.charterer) formattedResponse += `**Charterer:** ${data.charterer}\n`;
    if (data.charterer_address) formattedResponse += `**Charterer Address:** ${data.charterer_address}\n`;
    if (data.owners) formattedResponse += `**Owners:** ${data.owners}\n`;
    if (data.disponent_owners) formattedResponse += `**Disponent Owners:** ${data.disponent_owners}\n`;
    if (data.manager) formattedResponse += `**Manager:** ${data.manager}\n`;
    if (data.freight_beneficiary_address) formattedResponse += `**Freight Beneficiary Address:** ${data.freight_beneficiary_address}\n`;
    
    // Banking details
    if (data.account_owner || data.bank_address || data.bank_account_no || data.swift_code) {
      formattedResponse += `\n**Banking Details:**\n`;
      if (data.account_owner) formattedResponse += `- Account Owner: ${data.account_owner}\n`;
      if (data.bank_address) formattedResponse += `- Bank Address: ${data.bank_address}\n`;
      if (data.bank_account_no) formattedResponse += `- Account Number: ${data.bank_account_no}\n`;
      if (data.iban_no) formattedResponse += `- IBAN: ${data.iban_no}\n`;
      if (data.swift_code) formattedResponse += `- SWIFT Code: ${data.swift_code}\n`;
    }
    
    // Corresponding bank details
    if (data.corr_account_owner || data.corr_bank_address || data.corr_bank_account_no || data.corr_iban_no || data.corr_swift_code) {
      formattedResponse += `\n**Corresponding Bank Details:**\n`;
      if (data.corr_account_owner) formattedResponse += `- Account Owner: ${data.corr_account_owner}\n`;
      if (data.corr_bank_address) formattedResponse += `- Bank Address: ${data.corr_bank_address}\n`;
      if (data.corr_bank_account_no) formattedResponse += `- Account Number: ${data.corr_bank_account_no}\n`;
      if (data.corr_iban_no) formattedResponse += `- IBAN: ${data.corr_iban_no}\n`;
      if (data.corr_swift_code) formattedResponse += `- SWIFT Code: ${data.corr_swift_code}\n`;
    }
    
    // Commissions
    if (data.address_commission_pct || data.mercurius_commission_pct || data.brokerage_commission_pct) {
      formattedResponse += `\n**Commissions:**\n`;
      if (data.address_commission_pct) formattedResponse += `- Address Commission: ${data.address_commission_pct}%\n`;
      if (data.mercurius_commission_pct) formattedResponse += `- Mercurius Commission: ${data.mercurius_commission_pct}%\n`;
      if (data.brokerage_commission_pct) formattedResponse += `- Brokerage Commission: ${data.brokerage_commission_pct}%\n`;
    }
    
    if (data.additional_info) formattedResponse += `\n**Additional Information:** ${data.additional_info}\n`;
    
    if (data.error) {
      formattedResponse += `\n**⚠️ Processing Error:** ${data.error}\n`;
    }
    
    return formattedResponse;
  };

  const handleSend = async () => {
    if (input.trim() === '' && !fileName) return;

    // Add user message - modified to handle PDF case
    const messageText = fileName ? `Analyzing ${fileName}` : input;
    setMessages((prevMessages) => [
      ...prevMessages,
      { text: messageText, sender: 'user' },
    ]);
    setInput('');
    setIsLoading(true);

    try {
      // Create a FormData object to handle file and text data
      const formData = new FormData();
      formData.append('input', input);
      formData.append('deal_type', dealType); // Add deal type from URL

      // Append the file if it exists
      if (fileName) {
        const fileInput = document.getElementById('fileInput');
        const file = fileInput.files[0];
        formData.append('file', file);
      }

      // Add recap type if available and this is a trade recap
      if (isTradeRecap && recapType) {
        formData.append('recap_type', recapType);
      }

      // If we have a parent offer/recap and the user uploaded a source file,
      // ask the backend to persist the file as a DealDocument so it can be
      // viewed later. Without these IDs the backend keeps the legacy
      // "extract then delete" behaviour.
      if (fileName && persistOfferId) {
        formData.append('persist_source', 'true');
        formData.append('persist_offer_id', persistOfferId);
      }
      if (fileName && persistRecapId) {
        formData.append('persist_source', 'true');
        formData.append('persist_recap_id', persistRecapId);
      }

      // Use different endpoints based on whether this is a trade recap or not
      let endpoint;
      if (isTradeRecap) {
        if (recapType === 'Freight') {
          endpoint = '/api/offer/process-freight-input/';
        } else {
          endpoint = '/api/offer/process-recap-input/';
        }
      } else {
        endpoint = '/api/offer/process-input/';
      }
      
      const response = await axiosInstance.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Clear the file input after sending
      if (fileName) {
        setFileName('');
        const fileInput = document.getElementById('fileInput');
        fileInput.value = ''; // Reset the file input
      }

      // Check if there's an error in the response
      if (response.data.error) {
        const errorMsg = response.data.error === 'No offer details extracted from input.' 
          ? 'Please input Deal details text' 
          : getErrorMessage({ response }, 'Message could not be processed. Please try again.');
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: errorMsg, sender: 'assistant' },
        ]);
      } else {
        // Format the response data for display
        let formattedResponse;
        if (isTradeRecap) {
          if (recapType === 'Freight') {
            formattedResponse = formatFreightRecapResponse(response.data);
          } else {
            formattedResponse = formatTradeRecapResponse(response.data);
          }
        } else {
          formattedResponse = withReadingNote(formatOfferResponse(response.data), response.data);
        }

        // Add assistant response
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: formattedResponse, sender: 'assistant' },
        ]);

        // Log the full response data structure
        console.log('Full response from API:', response.data);
        
        // Log specifically the quality table data if it's an offer
        if (!isTradeRecap) {
          if (response.data['Quality Table']) {
            console.log('Old format Quality Table:', response.data['Quality Table']);
          }
          if (response.data.quality_table) {
            console.log('New format quality_table:', response.data.quality_table);
          }
        }

        // **Update the context with the response data**
        setOfferResponse(response.data);
      }
    } catch (error) {
      console.error('Error processing input:', error);
      // Display a helpful error message to the user
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: getErrorMessage(error, 'Message could not be processed. Please try again.'), sender: 'assistant' },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior (new line)
      handleSend();
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      
      // Create FormData and send immediately
      const formData = new FormData();
      formData.append('input', '');
      formData.append('file', file);
      formData.append('deal_type', dealType); // Add deal type from URL
      
      // Add recap type if available and this is a trade recap
      if (isTradeRecap && recapType) {
        formData.append('recap_type', recapType);
      }
      
      // Add user message for file upload
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: `Analyzing ${file.name}`, sender: 'user' },
      ]);
      setIsLoading(true);

      try {
        // Use different endpoints based on whether this is a trade recap or not
        let endpoint;
        if (isTradeRecap) {
          if (recapType === 'Freight') {
            endpoint = '/api/offer/process-freight-input/';
          } else {
            endpoint = '/api/offer/process-recap-input/';
          }
        } else {
          endpoint = '/api/offer/process-input/';
        }
        
        const response = await axiosInstance.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Clear the file input after sending
        setFileName('');
        event.target.value = ''; // Reset the file input

        // Handle response
        if (response.data.error) {
          const errorMsg = response.data.error === 'No offer details extracted from input.' 
            ? 'Please input Deal details text' 
            : getErrorMessage({ response }, 'Message could not be processed. Please try again.');
          setMessages((prevMessages) => [
            ...prevMessages,
            { text: errorMsg, sender: 'assistant' },
          ]);
        } else {
          let formattedResponse;
          if (isTradeRecap) {
            if (recapType === 'Freight') {
              formattedResponse = formatFreightRecapResponse(response.data);
            } else {
              formattedResponse = formatTradeRecapResponse(response.data);
            }
          } else {
            formattedResponse = withReadingNote(formatOfferResponse(response.data), response.data);
          }
          setMessages((prevMessages) => [
            ...prevMessages,
            { text: formattedResponse, sender: 'assistant' },
          ]);
          setOfferResponse(response.data);
        }
      } catch (error) {
        console.error('Error processing input:', error);
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: getErrorMessage(error, 'Message could not be processed. Please try again.'), sender: 'assistant' },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  /* Takes a file that arrived by drag-and-drop. The drop zone itself is
     components/shared/FileDropZone, so a dragged email attachment now produces
     an explanation instead of nothing at all. */
  const handleDroppedFile = async (file) => {
    if (file) {
      setFileName(file.name);
      
      // Update the hidden file input
      const fileInput = document.getElementById('fileInput');
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      fileInput.files = dataTransfer.files;

      // Create FormData and send immediately
      const formData = new FormData();
      formData.append('input', '');
      formData.append('file', file);
      formData.append('deal_type', dealType); // Add deal type from URL
      
      // Add recap type if available and this is a trade recap
      if (isTradeRecap && recapType) {
        formData.append('recap_type', recapType);
      }
      
      // Add user message for file upload
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: `Analyzing ${file.name}`, sender: 'user' },
      ]);
      setIsLoading(true);

      try {
        // Use different endpoints based on whether this is a trade recap or not
        let endpoint;
        if (isTradeRecap) {
          if (recapType === 'Freight') {
            endpoint = '/api/offer/process-freight-input/';
          } else {
            endpoint = '/api/offer/process-recap-input/';
          }
        } else {
          endpoint = '/api/offer/process-input/';
        }
        
        const response = await axiosInstance.post(endpoint, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Clear the file input after sending
        setFileName('');
        fileInput.value = ''; // Reset the file input

        // Handle response
        if (response.data.error) {
          const errorMsg = response.data.error === 'No offer details extracted from input.' 
            ? 'Please input Deal details text' 
            : getErrorMessage({ response }, 'Message could not be processed. Please try again.');
          setMessages((prevMessages) => [
            ...prevMessages,
            { text: errorMsg, sender: 'assistant' },
          ]);
        } else {
          let formattedResponse;
          if (isTradeRecap) {
            if (recapType === 'Freight') {
              formattedResponse = formatFreightRecapResponse(response.data);
            } else {
              formattedResponse = formatTradeRecapResponse(response.data);
            }
          } else {
            formattedResponse = withReadingNote(formatOfferResponse(response.data), response.data);
          }
          setMessages((prevMessages) => [
            ...prevMessages,
            { text: formattedResponse, sender: 'assistant' },
          ]);
          setOfferResponse(response.data);
        }
      } catch (error) {
        console.error('Error processing input:', error);
        setMessages((prevMessages) => [
          ...prevMessages,
          { text: getErrorMessage(error, 'Message could not be processed. Please try again.'), sender: 'assistant' },
        ]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const clearFile = (event) => {
    event.stopPropagation(); // Prevents the file explorer from opening
    setFileName('');
  };

  // Adjust the height of the textarea
  const autoResizeTextarea = () => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  // Auto-resize textarea when input changes
  useEffect(() => {
    autoResizeTextarea();
  }, [input]);

  // Scroll to the bottom when new messages are added
  useEffect(() => {
    // Use setTimeout to ensure DOM has updated before scrolling
    const scrollTimer = setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
    
    return () => clearTimeout(scrollTimer);
  }, [messages, isLoading]);

  return (
    <div className={isTradeRecap ? 'trade-recap-chat-window' : 'chat-window'}>
      <div className={isTradeRecap ? 'trade-recap-chat-messages' : 'chat-messages'}>
        {messages.length === 0 && !isLoading && (
          <div className="chat-placeholder">
            <div className="placeholder-content">
              <div className="options-grid">
                <div 
                  className={isTradeRecap ? 'trade-recap-option-rectangle' : 'option-rectangle'}
                  onClick={() => inputRef.current?.focus()}
                >
                  <span>
                    {isTradeRecap 
                      ? "Dump all your recap text here and Hamster will extract the necessary info"
                      : "Dump all your deal text here and Hamster will extract the necessary info"}
                  </span>
                </div>
                <div 
                  className={isTradeRecap ? 'trade-recap-option-rectangle' : 'option-rectangle'}
                  onClick={() => document.getElementById('fileInput').click()}
                >
                  <span>
                    {isTradeRecap
                      ? "Upload a PDF or Word document to extract trade recap details quickly"
                      : "Upload a PDF or Word document to extract offer details quickly"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
        {messages.map((message, index) => (
          <div
            key={index}
            className={`${isTradeRecap ? 'trade-recap-message' : 'chat-bubble'} ${
              message.sender === 'user' ? (isTradeRecap ? 'trade-recap-message-user' : 'user') : (isTradeRecap ? 'trade-recap-message-assistant' : 'assistant')
            }`}
          >
            <div dangerouslySetInnerHTML={{ __html: formatMessageLine(message.text) }} />
          </div>
        ))}
        {isLoading && (
          <div className={`${isTradeRecap ? 'trade-recap-message trade-recap-message-assistant' : 'chat-bubble assistant'}`}>
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}
        {/* Scroll target - always at the bottom of messages */}
        <div ref={messagesEndRef} style={{ float: 'left', clear: 'both' }} />
      </div>
      <div className={isTradeRecap ? 'trade-recap-chat-input-area' : 'chat-input-area'}>
        <textarea
          ref={inputRef}
          className={isTradeRecap ? 'trade-recap-chat-input' : 'chat-input'}
          placeholder={isTradeRecap ? "Build recap with Hamster..." : "Build deal with Hamster..."}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
          }}
          onKeyPress={handleInputKeyPress}
          disabled={isLoading}
        />
        <button
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          className={isTradeRecap ? 'trade-recap-send-button' : 'send-button'}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
      <FileDropZone
        className={isTradeRecap ? 'trade-recap-document-drop-box' : 'document-drop-box'}
        onClick={() => !fileName && document.getElementById('fileInput').click()}
        onFiles={(files) => handleDroppedFile(files[0])}
        onError={(message) => setMessages((prevMessages) => [
          ...prevMessages,
          { text: message, sender: 'assistant' },
        ])}
      >
        <span className={isTradeRecap ? 'trade-recap-file-name' : 'file-name'}>{fileName || 'Drop PDF or Word Document'}</span>
        {fileName && (
          <button className={isTradeRecap ? 'trade-recap-clear-file-button' : 'clear-file-button'} onClick={clearFile}>
            &times;
          </button>
        )}
        <input
          type="file"
          id="fileInput"
          accept=".pdf,.docx"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </FileDropZone>
    </div>
  );
}

export default ChatWindow;
